import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export interface FeatureUsage {
  feature_key: string;
  label: string | null;
  nav_group: string | null;
  events: number;
  users: number;
  prior_events: number;
  prior_users: number;
  exceptions: number;
  delta_events: number;
}

export interface UserUsage {
  user_id: string;
  name: string | null;
  email: string | null;
  events: number;
  features_used: number;
  by_feature: Record<string, number>;
}

export interface DayPoint {
  day: string;
  events: number;
  users: number;
}

export interface Overview {
  period_start: string;
  period_end: string;
  days: number;
  total_events: number;
  active_users: number;
  features: FeatureUsage[];
  users: UserUsage[];
  daily: DayPoint[];
  refreshed_at: string | null;
  stale: boolean;
}

export interface FeatureUser {
  user_id: string;
  name: string | null;
  email: string | null;
  events: number;
  last_seen: string | null;
}

export interface PathUsage {
  path: string;
  events: number;
}

export interface FeatureError {
  kind: string;
  message: string;
  events: number;
  last_seen: string | null;
}

export interface FeatureDetail {
  feature_key: string;
  period_start: string;
  period_end: string;
  days: number;
  events: number;
  users: number;
  prior_events: number;
  prior_users: number;
  delta_events: number;
  last_seen: string | null;
  daily: DayPoint[];
  by_user: FeatureUser[];
  by_path: PathUsage[];
  errors: FeatureError[];
  expanded: boolean;
  posthog_url: string | null;
  refreshed_at: string | null;
  stale: boolean;
}

/**
 * Drill-down for one feature. `expanded` costs two extra PostHog queries
 * (route breakdown + errors), so the quick view deliberately doesn't ask for
 * them — a row click has to feel instant.
 */
export function useFeatureDetail(
  featureKey: string | null,
  days: number,
  expanded: boolean,
) {
  return useQuery({
    queryKey: ["posthog", "feature", featureKey, days, expanded],
    queryFn: () =>
      apiFetch<FeatureDetail>(
        `/posthog/features/${featureKey}?days=${days}&expanded=${expanded}`,
      ),
    enabled: Boolean(featureKey),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export interface UserGoal {
  user_id: string;
  name: string | null;
  email: string | null;
  cadence: string;
  target_events: number | null;
  expectation: string | null;
  is_active: boolean;
}

export interface FeatureGoal {
  feature_key: string;
  label: string;
  nav_group: string | null;
  owner_user_id: string | null;
  purpose: string | null;
  key_action_event: string | null;
  cadence: string;
  target_active_users: number | null;
  target_events: number | null;
  is_active: boolean;
  notes: string | null;
  user_goals: UserGoal[];
}

export interface PostHogSettings {
  secret_store_ready: boolean;
  project_key_configured: boolean;
  project_key_source: string | null;
  project_key: string | null;
  personal_key_configured: boolean;
  personal_key_source: string | null;
  capture_enabled: boolean;
  client_api_host: string;
  project_id: string | null;
  read_back_ready: boolean;
}

export interface ClientConfig {
  enabled: boolean;
  project_key: string | null;
  api_host: string;
  ui_host: string;
}

/**
 * The same bootstrap config the analytics client reads. Readable by every
 * authenticated user (unlike /settings, which is superuser-only), so the setup
 * notice can tell a non-superuser whether events are being captured.
 */
export function usePostHogClientConfig() {
  return useQuery({
    queryKey: ["posthog", "config"],
    queryFn: () => apiFetch<ClientConfig>("/posthog/config"),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useOverview(days: number) {
  return useQuery({
    queryKey: ["posthog", "overview", days],
    queryFn: () => apiFetch<Overview>(`/posthog/overview?days=${days}`),
    // The backend caches for an hour; don't re-ask on every tab switch.
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useRefreshOverview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) =>
      apiFetch<Overview>(`/posthog/overview?days=${days}&refresh=true`),
    onSuccess: (data, days) => {
      qc.setQueryData(["posthog", "overview", days], data);
    },
  });
}

export function useGoals() {
  return useQuery({
    queryKey: ["posthog", "goals"],
    queryFn: () => apiFetch<FeatureGoal[]>("/posthog/goals"),
    staleTime: 60_000,
  });
}

export function useSaveGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ featureKey, ...body }: { featureKey: string } & Partial<FeatureGoal>) =>
      apiFetch<FeatureGoal>(`/posthog/goals/${featureKey}`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["posthog", "goals"] });
    },
  });
}

export function useSaveUserGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      featureKey,
      userId,
      ...body
    }: {
      featureKey: string;
      userId: string;
    } & Partial<UserGoal>) =>
      apiFetch<FeatureGoal>(`/posthog/goals/${featureKey}/users/${userId}`, {
        method: "PUT",
        body,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["posthog", "goals"] });
    },
  });
}

export function useDeleteUserGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ featureKey, userId }: { featureKey: string; userId: string }) =>
      apiFetch<void>(`/posthog/goals/${featureKey}/users/${userId}`, { method: "DELETE" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["posthog", "goals"] });
    },
  });
}

export function usePostHogSettings(enabled: boolean) {
  return useQuery({
    queryKey: ["posthog", "settings"],
    queryFn: () => apiFetch<PostHogSettings>("/posthog/settings"),
    enabled,
    retry: false,
  });
}

export interface SettingsUpdate {
  project_key?: string;
  personal_api_key?: string;
  project_id?: string;
  client_api_host?: string;
  capture_enabled?: boolean;
}

export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SettingsUpdate) =>
      apiFetch<PostHogSettings>("/posthog/settings", { method: "PUT", body }),
    onSuccess: (data) => {
      qc.setQueryData(["posthog", "settings"], data);
      // The SPA's own analytics config may have just changed.
      void qc.invalidateQueries({ queryKey: ["posthog", "config"] });
    },
  });
}
